import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import { Form, Field as RFFField } from 'react-final-form';
import type { FormApi } from 'final-form';
import { Alert, Checkbox, Label } from '@manulife/mux';
import { StepProps, SelectOption, CreateAgencyFormData } from '../types';
import { MUXTextInput, MUXSelect } from '../components/FormWrappers';
import {
  isNonEmptyString,
  isValidEmail,
  isValidProvince
} from '../validation';
import {
  BANK_DROPDOWN_OPTIONS,
  OTHER_BANK_VALUE,
  isOtherBank,
  getBankByInstitution,
  isValidInstitutionNumber,
  isValidTransitNumber,
  isValidAccountNumber,
  isValidCRANumber,
  isValidQuebecNumber,
  BANKING_VALIDATION,
  CRA_NUMBER_LENGTH,
  QUEBEC_NUMBER_LENGTH,
  PROVINCES
} from '@trip-ux/constants';
import { Contract, Banking, LegalEntity } from '@trip-ux/redux-store';

// --- Types ---

interface Step3FormValues {
  bankSelection: string;
  bankName: string;
  bankTransit: string;
  bankNumber: string;
  accountNumber: string;
  bankProvince: string;
  legalName: string;
  tradeName: string;
  CRANumber: string;
  QuebecNumber: string;
  BusinessNumber: string;
  HSTNumber: string;
  QSTNumber: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  hasHST: boolean | string;
}

// --- Constants ---

/** Fallback to empty string — absorbs || branching for SonarQube complexity */
const str = (val: string | undefined | null): string => val || '';

const POSTAL_CODE_REGEX = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

const PROVINCE_OPTIONS: SelectOption[] = Object.entries(PROVINCES).map(([code, label]) => ({
  label,
  value: code
}));

// --- Validation Helpers ---

const validateRequiredLegalFields = (
  values: Step3FormValues,
  errors: Record<string, string>
): void => {
  if (!isNonEmptyString(values.legalName)) {
    errors.legalName = 'Legal name is required';
  }
  if (!isNonEmptyString(values.addressLine1)) {
    errors.addressLine1 = 'Address is required';
  }
  if (!isNonEmptyString(values.city)) {
    errors.city = 'City is required';
  }
  if (!isNonEmptyString(values.province)) {
    errors.province = 'Province is required';
  } else if (!isValidProvince(values.province)) {
    errors.province = 'Invalid province';
  }
  if (!isNonEmptyString(values.postalCode)) {
    errors.postalCode = 'Postal code is required';
  } else if (!POSTAL_CODE_REGEX.test(values.postalCode)) {
    errors.postalCode = 'Invalid postal code format (e.g., A1A 1A1)';
  }
};

const validateLegalFieldFormats = (
  values: Step3FormValues,
  errors: Record<string, string>
): void => {
  if (isNonEmptyString(values.email) && !isValidEmail(values.email)) {
    errors.email = 'Invalid email format';
  }
  if (isNonEmptyString(values.CRANumber) && !isValidCRANumber(values.CRANumber)) {
    errors.CRANumber = `CRA Number must be exactly ${CRA_NUMBER_LENGTH} digits`;
  }
  if (isNonEmptyString(values.QuebecNumber) && !isValidQuebecNumber(values.QuebecNumber)) {
    errors.QuebecNumber = `Quebec Number must be exactly ${QUEBEC_NUMBER_LENGTH} digits`;
  }
};

const validateLegalEntityFields = (
  values: Step3FormValues,
  errors: Record<string, string>
): void => {
  validateRequiredLegalFields(values, errors);
  validateLegalFieldFormats(values, errors);
};

const validateBankingFields = (
  values: Step3FormValues,
  errors: Record<string, string>
): void => {
  if (isNonEmptyString(values.bankTransit) && !isValidTransitNumber(values.bankTransit)) {
    errors.bankTransit = `Transit Number must be exactly ${BANKING_VALIDATION.TRANSIT_NUMBER_LENGTH} digits`;
  }
  if (isNonEmptyString(values.accountNumber) && !isValidAccountNumber(values.accountNumber)) {
    errors.accountNumber = `Account Number must be ${BANKING_VALIDATION.MIN_ACCOUNT_NUMBER_LENGTH}-${BANKING_VALIDATION.MAX_ACCOUNT_NUMBER_LENGTH} digits`;
  }
  if (isOtherBank(values.bankSelection) && isNonEmptyString(values.bankNumber) && !isValidInstitutionNumber(values.bankNumber)) {
    errors.bankNumber = `Institution Number must be exactly ${BANKING_VALIDATION.INSTITUTION_NUMBER_LENGTH} digits`;
  }
  if (isNonEmptyString(values.bankProvince) && !isValidProvince(values.bankProvince)) {
    errors.bankProvince = 'Invalid bank province';
  }
};

const validateStep3 = (values: Step3FormValues): Record<string, string> => {
  const errors: Record<string, string> = {};
  validateLegalEntityFields(values, errors);
  validateBankingFields(values, errors);
  return errors;
};

// --- Data Builder Helpers ---

const getInitialBankSelection = (bankNumber: string | undefined): string => {
  if (!bankNumber) {
    return '';
  }
  const bank = getBankByInstitution(bankNumber);
  return bank ? bankNumber : OTHER_BANK_VALUE;
};

const buildBankingInitialValues = (banking: Banking | undefined) => ({
  bankSelection: getInitialBankSelection(banking?.bankNumber),
  bankName: banking?.bankName || '',
  bankTransit: banking?.bankTransit || '',
  bankNumber: banking?.bankNumber || '',
  accountNumber: banking?.accountNumber || '',
  bankProvince: banking?.bankProvince || ''
});

const buildLegalInitialValues = (legal: Partial<LegalEntity> | undefined) => ({
  legalName: str(legal?.legalName),
  tradeName: str(legal?.tradeName),
  CRANumber: str(legal?.CRANumber),
  QuebecNumber: str(legal?.QuebecNumber),
  BusinessNumber: str(legal?.BusinessNumber),
  HSTNumber: str(legal?.HSTNumber),
  QSTNumber: str(legal?.QSTNumber),
  email: str(legal?.email),
  addressLine1: str(legal?.addressLine1),
  addressLine2: str(legal?.addressLine2),
  city: str(legal?.city),
  province: str(legal?.province),
  postalCode: str(legal?.postalCode),
  country: legal?.country ?? 'CA',
  hasHST: legal?.hasHST ?? false
});

const buildInitialValues = (formData: CreateAgencyFormData): Step3FormValues => {
  const contract = formData.contract?.[0] || {};
  return {
    ...buildBankingInitialValues(contract.banking),
    ...buildLegalInitialValues(contract.legalEntity)
  } as Step3FormValues;
};

const deriveBankInfo = (values: Step3FormValues): { name: string; number: string } => {
  if (values.bankSelection && !isOtherBank(values.bankSelection)) {
    const selectedBank = getBankByInstitution(values.bankSelection);
    if (selectedBank) {
      return { name: selectedBank.name, number: selectedBank.institutionNumber };
    }
  }
  return { name: values.bankName || '', number: values.bankNumber || '' };
};

const buildBankingData = (values: Step3FormValues) => {
  const bankInfo = deriveBankInfo(values);
  return {
    bankName: bankInfo.name,
    bankTransit: values.bankTransit || '',
    bankNumber: bankInfo.number,
    accountNumber: values.accountNumber || '',
    bankProvince: values.bankProvince || ''
  };
};

const isHSTEnabled = (val: boolean | string): boolean =>
  val === true || val === 'true';

const buildLegalEntityData = (values: Step3FormValues) => ({
  legalName: values.legalName?.toUpperCase(),
  tradeName: str(values.tradeName),
  HSTNumber: isHSTEnabled(values.hasHST) ? str(values.HSTNumber) : '',
  hasHST: isHSTEnabled(values.hasHST),
  CRANumber: str(values.CRANumber),
  BusinessNumber: str(values.BusinessNumber),
  QSTNumber: str(values.QSTNumber),
  QuebecNumber: str(values.QuebecNumber),
  email: str(values.email),
  addressLine1: values.addressLine1,
  addressLine2: str(values.addressLine2),
  city: values.city,
  province: values.province,
  postalCode: values.postalCode,
  country: values.country ?? 'CA'
});

const EMPTY_BANKING: Banking = {
  bankName: '',
  bankTransit: '',
  bankNumber: '',
  accountNumber: '',
  bankProvince: ''
};

const buildContract = (
  values: Step3FormValues,
  contractTerms: Partial<Contract>
): Contract => ({
  ...contractTerms,
  banking: contractTerms.commissionRecipient === false ? EMPTY_BANKING : buildBankingData(values),
  legalEntity: buildLegalEntityData(values)
});

// --- Sub-Components ---

const HSTCheckboxField: FC = () => (
  <RFFField name="hasHST" type="checkbox">
    {({ input }) => (
      <Label className="mux-mt-8">
        <Checkbox
          checked={!!input.checked}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => input.onChange(e)}
        />
        Has HST
      </Label>
    )}
  </RFFField>
);

const BankingFields: FC = () => (
  <fieldset className="mux-mb-6">
    <legend className="mux-text-lg mux-font-semibold mux-mb-4">Banking Information</legend>
    <div className="mux-grid mux-grid-cols-1 tablet:mux-grid-cols-2 mux-gap-4">
      <div className="mux-col-span-full">
        <RFFField name="bankSelection" component={MUXSelect} label="Select Bank"
          options={BANK_DROPDOWN_OPTIONS} placeholder="Select a financial institution" />
      </div>
      <RFFField name="bankSelection" subscription={{ value: true }}>
        {({ input: { value: bankSelectionValue } }) => (
          isOtherBank(bankSelectionValue) ? (
            <>
              <div className="mux-col-span-1">
                <RFFField name="bankName" component={MUXTextInput} label="Bank Name"
                  type="text" placeholder="Enter bank name" />
              </div>
              <div className="mux-col-span-1">
                <RFFField name="bankNumber" component={MUXTextInput} label="Institution Number"
                  type="text" placeholder="3 digits" maxLength={3} />
              </div>
            </>
          ) : null
        )}
      </RFFField>
      <div className="mux-col-span-1">
        <RFFField name="bankTransit" component={MUXTextInput} label="Transit Number"
          type="text" placeholder="5 digits" maxLength={5} />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="bankProvince" component={MUXSelect} label="Bank Province"
          options={PROVINCE_OPTIONS} />
      </div>
      <div className="mux-col-span-full">
        <RFFField name="accountNumber" component={MUXTextInput} label="Account Number"
          type="text" placeholder="7-12 digits" maxLength={12} />
      </div>
    </div>
  </fieldset>
);

const LegalEntityFields: FC = () => (
  <fieldset className="mux-mb-6">
    <legend className="mux-text-lg mux-font-semibold mux-mb-4">Legal Entity Information</legend>
    <div className="mux-grid mux-grid-cols-1 tablet:mux-grid-cols-2 mux-gap-4">
      <div className="mux-col-span-full">
        <RFFField name="legalName" component={MUXTextInput} label="Legal Name" type="text" required />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="tradeName" component={MUXTextInput} label="Trade Name" type="text" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="email" component={MUXTextInput} label="Email" type="email" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="CRANumber" component={MUXTextInput} label="CRA Number" type="text" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="BusinessNumber" component={MUXTextInput} label="Business Number" type="text" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="QuebecNumber" component={MUXTextInput} label="Quebec Number" type="text" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="QSTNumber" component={MUXTextInput} label="QST Number" type="text" />
      </div>
      <div className="mux-col-span-1">
        <HSTCheckboxField />
      </div>
      <RFFField name="hasHST" subscription={{ value: true }}>
        {({ input: { value: hasHSTValue } }) =>
          hasHSTValue ? (
            <div className="mux-col-span-1">
              <RFFField name="HSTNumber" component={MUXTextInput} label="HST Number" type="text" />
            </div>
          ) : null
        }
      </RFFField>
      <div className="mux-col-span-full">
        <RFFField name="addressLine1" component={MUXTextInput} label="Address Line 1" type="text" required />
      </div>
      <div className="mux-col-span-full">
        <RFFField name="addressLine2" component={MUXTextInput} label="Address Line 2" type="text" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="city" component={MUXTextInput} label="City" type="text" required />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="province" component={MUXSelect} label="Province" options={PROVINCE_OPTIONS} required />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="postalCode" component={MUXTextInput} label="Postal Code" type="text" required />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="country" component={MUXTextInput} label="Country" type="text" disabled />
      </div>
    </div>
  </fieldset>
);

// --- Main Component ---

const Step3BankingLegal: FC<StepProps> = ({ formData, onNext, formRef }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formInstanceRef = useRef<FormApi<Step3FormValues> | null>(null);
  const initialValuesRef = useRef<Step3FormValues | null>(null);

  if (!initialValuesRef.current) {
    initialValuesRef.current = buildInitialValues(formData);
  }

  const handleSubmit = (values: Step3FormValues) => {
    const newErrors = validateStep3(values);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const contractTerms = formData.contractTerms || {};
    onNext({ contract: [buildContract(values, contractTerms)] });
  };

  const handleAlertClose = useCallback(() => {
    setErrors({});
  }, []);

  useEffect(() => {
    if (formRef && formInstanceRef.current) {
      formRef({ submit: () => formInstanceRef.current?.submit() });
    }
  }, [formRef]);

  const renderForm = useCallback(({ handleSubmit: formSubmit, form }: { handleSubmit: () => void; form: FormApi<Step3FormValues> }) => {
    formInstanceRef.current = form;
    return (
      <form onSubmit={formSubmit} className="step-form">

        {Object.keys(errors).length > 0 && (
          <Alert severity="alert" onClose={handleAlertClose}>
            Please fix the errors below before proceeding.
          </Alert>
        )}

        {formData.contractTerms?.commissionRecipient !== false && <BankingFields />}
        <LegalEntityFields />
      </form>
    );
  }, [errors, handleAlertClose, formData.contractTerms?.commissionRecipient]);

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validateStep3}
      initialValues={initialValuesRef.current}
      render={renderForm}
    />
  );
};

export default Step3BankingLegal;
